import { Spinner } from 'core/ui';
import React from 'react';
import { CompositionState } from './Composition';
import { StepComponentProps } from './StepDefinition';


type StepLoaderProps<TData extends Record<string, unknown>> = StepComponentProps & {
	getData: (currentComposition: CompositionState) => Promise<TData>;
	getNextComposition?: (args: {
		data: TData;
		composition: CompositionState;
	}) => CompositionState;
	children: (props: StepComponentProps & TData) => React.ReactNode;
};

/*
 * StepLoader centraliza la obtención de los datos iniciales de un step y evita
 * que el paso se renderice con un estado global todavía desalineado.
 *
 * La idea es que, una vez cargado ese initial data, el loader pueda determinar
 * si con esa información hay que ajustar el estado global del wizard. Si hace
 * falta modificarlo, no renderiza todavía a los hijos: primero actualiza el
 * estado del wizard y espera a que ese nuevo estado vuelva a bajar por props.
 *
 * Solo cuando ese estado ya está estabilizado renderiza el contenido del step.
 * De esta forma, cada paso se monta siempre con el composition final y no con
 * una versión intermedia.
 *
 * Ese estado debe vivir en el wizard porque es el estado global que usamos para
 * ir pintando el preview y para mantener sincronizado lo que se ve en el step
 * actual con la composición completa del producto.
 */

export function StepLoader<TData extends Record<string, unknown>>({
	getData,
	getNextComposition,
	children,
	...props
}: StepLoaderProps<TData>) {

	const [data, setData] = React.useState<TData | null>(null);
	const { composition, setComposition } = props;

	React.useEffect(() => {
		let cancelled = false;

		const run = async () => {

			const loadedData = await getData(composition);
			if (cancelled) return;

			if (getNextComposition) {
				const nextComposition = getNextComposition({
					data: loadedData,
					composition: composition,
				});

				if (nextComposition !== composition) {
					setComposition(nextComposition);
					return;
				}
			}

			setData(loadedData);
		};

		void run();

		return () => {
			cancelled = true;
		};
	}, [getData, getNextComposition, composition, setComposition]);

	if (!data) {
		return <Spinner />;
	}

	return <>{children({ ...props, ...data })}</>;
}